import { Injectable, Type } from "@angular/core";
import { ILookupService } from "../../ILookupService";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BaseLookupService } from "../../impl/BaseLookupService";

import { IAutoCompleteLabelValueLookupService } from "../IAutoCompleteLabelValueLookupService";
import {
  AutoCompleteLabelValue,
  AutoCompleteLabelValueCompareFn,
} from "../../../model/core/AutoCompleteLabelValue";

import { Iterator, TreeSet, TreeMultiSet } from "jstreemap";

export abstract class AutoCompleteLabelValueLookupService<K, V extends CubiqTS.ConcreteEncodable>
  extends BaseLookupService<K, V>
  implements IAutoCompleteLabelValueLookupService
{
  protected idLabelValueSet: TreeSet<AutoCompleteLabelValue>;
  protected nameLabelValueSet: TreeMultiSet<AutoCompleteLabelValue>;

  constructor(protected lookupType: Type<V>) {
    super(lookupType);

    this.idLabelValueSet = new TreeMultiSet();
    this.idLabelValueSet.compareFunc = AutoCompleteLabelValueCompareFn;

    this.nameLabelValueSet = new TreeMultiSet();
    this.nameLabelValueSet.compareFunc = AutoCompleteLabelValueCompareFn;
  }

  abstract itemAsIdLabelValue(item: V): AutoCompleteLabelValue;
  abstract itemAsNameLabelValue(item: V): AutoCompleteLabelValue;

  deleteItem(item: V): void {
    super.deleteItem(item);

    this.idLabelValueSet.delete(this.itemAsIdLabelValue(item));

    this.nameLabelValueSet.delete(this.itemAsNameLabelValue(item));
  }

  mapItemInTreeSet(item: AutoCompleteLabelValue, set) {
    set.insertOrReplace(item);
  }

  mapItemInTreeSetByValue(item: AutoCompleteLabelValue, set) {
    let it = set.find(item);

    // clear old (value unique, label may duplicate)
    while (!it.equals(set.end())) {
      if (it.key.label != item.label) {
        break;
      }

      if (it.key.value == item.value) {
        let erasable = it;
        it.next();
        set.erase(erasable);
      } else {
        it.next();
      }
    }

    set.add(item);
  }

  private getLabelValue(
    set: TreeSet<AutoCompleteLabelValue> | TreeMultiSet<AutoCompleteLabelValue>,
    opts?: { prefix?: string; skip?: number; limit?: number },
  ): AutoCompleteLabelValue[] {
    opts = opts || {
      prefix: null,
      skip: 0,
      limit: set.size,
    };

    let arr = [...Array(opts.limit)];
    let i = 0;
    let prefix = opts.prefix !== null ? opts.prefix.toLowerCase() : null;

    for (let it = set.begin(); !it.equals(set.end()); it.next()) {
      if (opts.skip > 0) {
        if (
          prefix &&
          it.key !== null &&
          typeof it.key.label === "string" &&
          it.key.label.toLowerCase().indexOf(prefix) === -1
        ) {
          opts.skip--;
        }

        continue;
      }

      if (i == opts.limit) break;

      if (
        prefix &&
        it.key !== null &&
        typeof it.key.label === "string" &&
        it.key.label.toLowerCase().indexOf(prefix) === -1
      ) {
        continue;
      }

      arr[i] = it.key;
      i++;
    }

    return arr.slice(0, i);
  }

  getLabelValueById(opts?: {
    prefix?: string;
    skip?: number;
    limit?: number;
  }): AutoCompleteLabelValue[] {
    return this.getLabelValue(this.idLabelValueSet, opts);
  }

  getLabelValueByName(opts: {
    prefix?: string;
    skip?: number;
    limit?: number;
  }): AutoCompleteLabelValue[] {
    return this.getLabelValue(this.nameLabelValueSet, opts);
  }
}
